/**
 * Client service for Jobs sector operations (SO-P117).
 * Wraps callables for job sites and customer contacts, and provides
 * Firestore subscriptions for customers, sites, and installed assets.
 */

import {
  collection,
  onSnapshot,
  query,
  where,
  type Unsubscribe,
} from 'firebase/firestore'
import { firestoreDb } from './firebase'
import { callable } from './callable'
import type {
  OpsPlace,
  OpsAsset,
  OpsCustomer,
  CreateJobSiteInput,
  CreateJobSiteResult,
  UpdateJobSiteInput,
  UpdateJobSiteResult,
  ListCustomerContactsInput,
  ListCustomerContactsResult,
  ScheduleJobInput,
  ScheduleJobResult,
  RaiseRevisitInput,
  RaiseRevisitResult,
  ListJobBoardInput,
  ListJobBoardResult,
} from '../types/ops'

export async function scheduleJob(
  input: ScheduleJobInput,
): Promise<ScheduleJobResult> {
  return callable<ScheduleJobInput, ScheduleJobResult>('scheduleJob', input)
}

export async function raiseRevisit(
  input: RaiseRevisitInput,
): Promise<RaiseRevisitResult> {
  return callable<RaiseRevisitInput, RaiseRevisitResult>('raiseRevisit', input)
}

export async function listJobBoard(
  input: ListJobBoardInput,
): Promise<ListJobBoardResult> {
  return callable<ListJobBoardInput, ListJobBoardResult>('listJobBoard', input)
}

export async function createJobSite(
  input: CreateJobSiteInput,
): Promise<CreateJobSiteResult> {
  return callable<CreateJobSiteInput, CreateJobSiteResult>('createJobSite', input)
}

export async function updateJobSite(
  input: UpdateJobSiteInput,
): Promise<UpdateJobSiteResult> {
  return callable<UpdateJobSiteInput, UpdateJobSiteResult>('updateJobSite', input)
}

export async function listCustomerContacts(
  input: ListCustomerContactsInput,
): Promise<ListCustomerContactsResult> {
  return callable<ListCustomerContactsInput, ListCustomerContactsResult>(
    'listCustomerContacts',
    input,
  )
}

/**
 * Subscribes to active customers in the Jobs sector.
 */
export function subscribeJobsCustomers(
  workspaceId: string,
  onUpdate: (customers: OpsCustomer[]) => void,
  onError?: (err: Error) => void,
  limitCount = 50,
  search?: string,
): Unsubscribe {
  if (!workspaceId) {
    onUpdate([])
    return () => {}
  }

  const coll = collection(firestoreDb, 'ops_customers')
  const q = query(
    coll,
    where('workspaceId', '==', workspaceId),
    where('sectors', 'array-contains', 'jobs'),
  )

  return onSnapshot(
    q,
    (snap) => {
      let items: OpsCustomer[] = snap.docs.map((d) => ({
        id: d.id,
        ...(d.data() as Omit<OpsCustomer, 'id'>),
      }))

      items = items.filter((c) => c.status !== 'ARCHIVED')

      if (search && search.trim()) {
        const queryTerm = search.trim().toLowerCase()
        items = items.filter(
          (c) =>
            c.displayName.toLowerCase().includes(queryTerm) ||
            c.tags?.some((t) => t.toLowerCase().includes(queryTerm)),
        )
      }

      items.sort((a, b) => a.displayName.localeCompare(b.displayName))

      if (limitCount > 0 && items.length > limitCount) {
        items = items.slice(0, limitCount)
      }

      onUpdate(items)
    },
    onError,
  )
}

/**
 * Subscribes to service areas for the Jobs sector.
 */
export function subscribeJobsServiceAreas(
  workspaceId: string,
  onUpdate: (areas: OpsPlace[]) => void,
  onError?: (err: Error) => void,
): Unsubscribe {
  if (!workspaceId) {
    onUpdate([])
    return () => {}
  }

  const coll = collection(firestoreDb, 'ops_places')
  const q = query(
    coll,
    where('workspaceId', '==', workspaceId),
    where('sector', '==', 'jobs'),
    where('level', '==', 'SERVICE_AREA'),
  )

  return onSnapshot(
    q,
    (snap) => {
      const items: OpsPlace[] = snap.docs
        .map((d) => ({ id: d.id, ...(d.data() as Omit<OpsPlace, 'id'>) }))
        .filter((p) => p.archived !== true)
      items.sort((a, b) => (a.name || '').localeCompare(b.name || ''))
      onUpdate(items)
    },
    onError,
  )
}

/**
 * Subscribes to job sites belonging to a customer.
 */
export function subscribeSitesByCustomer(
  workspaceId: string,
  customerId: string,
  onUpdate: (places: OpsPlace[]) => void,
  onError?: (err: Error) => void,
): Unsubscribe {
  if (!workspaceId || !customerId) {
    onUpdate([])
    return () => {}
  }

  const coll = collection(firestoreDb, 'ops_places')
  const q = query(
    coll,
    where('workspaceId', '==', workspaceId),
    where('sector', '==', 'jobs'),
    where('level', '==', 'SITE'),
    where('customerId', '==', customerId),
  )

  return onSnapshot(
    q,
    (snap) => {
      const items: OpsPlace[] = snap.docs
        .map((d) => ({ id: d.id, ...(d.data() as Omit<OpsPlace, 'id'>) }))
        .filter((p) => p.archived !== true)
      items.sort((a, b) => (a.name || '').localeCompare(b.name || ''))
      onUpdate(items)
    },
    onError,
  )
}

/**
 * Subscribes to all installed assets belonging to a customer.
 */
export function subscribeAssetsByCustomerId(
  workspaceId: string,
  customerId: string,
  onUpdate: (assets: OpsAsset[]) => void,
  onError?: (err: Error) => void,
): Unsubscribe {
  if (!workspaceId || !customerId) {
    onUpdate([])
    return () => {}
  }

  const coll = collection(firestoreDb, 'ops_assets')
  const q = query(
    coll,
    where('workspaceId', '==', workspaceId),
    where('customerId', '==', customerId),
  )

  return onSnapshot(
    q,
    (snap) => {
      const items: OpsAsset[] = snap.docs
        .map((d) => ({ id: d.id, ...(d.data() as Omit<OpsAsset, 'id'>) }))
        .filter((a) => a.status !== 'RETIRED')
      items.sort((a, b) => (a.name || '').localeCompare(b.name || ''))
      onUpdate(items)
    },
    onError,
  )
}
